import * as XLSX from 'xlsx';
import { IDailySnapshot } from '../../@types/Dashboard/IDailySnapshot';
import { format } from 'date-fns';
import { formatDateSnapshot, parseDateString } from '../date/formatedDate';

type ITransformedDailySnapshot = Record<string, any>;


const collectKeysInOrder = (data: ITransformedDailySnapshot[]) => {
  const keys: string[] = [];
  for (const row of data) {
    for (const k of Object.keys(row)) {
      if (!keys.includes(k)) keys.push(k);
    }
  }
  return keys;
};

const computeColumnWidthsFromFirstRow = (
  data: ITransformedDailySnapshot[],
  options?: { padding?: number; min?: number; max?: number }
) => {
  const padding = options?.padding ?? 2;
  const min = options?.min ?? 8;
  const max = options?.max ?? 50;

  if (!data || data.length === 0) return [];

  const keys = collectKeysInOrder(data);
  const firstRow = data[0] || {};

  return keys.map((key) => {
    const headerLen = key ? String(key).length : 0;
    const val = firstRow[key];
    const valLen = val === null || val === undefined ? 0 : String(val).length;
    const w = Math.max(headerLen, valLen) + padding;
    const bounded = Math.min(Math.max(w, min), max);
    return { wch: bounded };
  });
};

const transformDataForExport = (data: IDailySnapshot[]): ITransformedDailySnapshot[] => {
  return data.map((item) => {
    const date = formatDateSnapshot(item.snapshot_date);
    const month = date.split('-')[1];
    const year = date.split('-')[2];

    const frequencyMap: Record<string, string> = {
      'WEEKLY': 'Semanal',
      'BIWEEKLY': 'Bisemanal',
      'MONTHLY': 'Mensal'
    };

    const frequency = item.micro_area_frequency ? frequencyMap[item.micro_area_frequency] || item.micro_area_frequency : '';

    const initialsMicroArea = item.micro_area_initials ? `${item.farm_initials}-${item.area_initials}-${item.micro_area_initials}` : "";
    const initialsBatch = item.batch_initials ? `${item.farm_initials}-${item.area_initials}-${item.batch_initials}` : "";

    const transformed: ITransformedDailySnapshot = {
      'Ano': year,
      'Mês': month,
      'Data': formatDateSnapshot(item.snapshot_date),
      'Fazenda': item.farm_name,
      'Retiro': item.area_name,
      'Pasto': initialsMicroArea,
      'Lote': initialsBatch,
      'Grupo de manejo': item.micro_area_management_group_initials,
      'Dias de ocupação': item.days_grazing,
      'Dias de descanso': item.days_without_grazing,
      'Área do pasto (ha)': item.micro_area_acreage,
      'Tipo de Pastejo': item.micro_area_type,
      'Tipo de Monitoramento': item.micro_area_monitoring_type,
      'Valor monitoramento': item.monitor_value,
      'Valor de entrada cadastrado': item.micro_area_requirement_in,
      'Valor de saída cadastrado': item.micro_area_requirement_out,
      'Frequencia de monitoramento': frequency,
      'Tipo de movimentação': item.micro_area_grazin_begin ? 'Saída' : 'Entrada',
      '0a8 F': item.qty_0_8_female,
      '9a12 F': item.qty_9_12_female,
      '13a24 F': item.qty_13_24_female,
      '25a36 F': item.qty_25_36_female,
      '>37 F': item.qty_37_female,
      '0a8 M': item.qty_0_8_male,
      '9a12 M': item.qty_9_12_male,
      '13a24 M': item.qty_13_24_male,
      '25a36 M': item.qty_25_36_male,
      '>37 M': item.qty_37_male,
      'Touros': item.qty_adult_male || 0,
      'Quantidade de animais no lote': item.total_from_category,
      'Peso médio': item.average_weight,
      'Peso Total': item.total_weight,
      'UA': item.ua,
      'Tx lotação instantânea': item.instant_capacity_rate,
      'Produção (kg de PV)': item.cumulative_productivity,
      'Produtividade (kg de PV/ha)': item.productivity,
      'Suplementação no cocho (kg)': item.supplement_trough_quantity_total,
      'Classificação do produto no cocho': item.supplement_trough_classification,
      'Produto no cocho': item.supplement_trough_trade_name,
      'Classificação do produto no depósito 1': item.supplement_stock_classification_1,
      'Produto depósito 1': item.supplement_stock_trade_name_1,
      'Quantidade de suplemento no depósito 1': item.supplement_stock_balance_1
    };

    const itemAny = item as any;
    let productIndex = 2;
    while (
      itemAny[`supplement_stock_classification_${productIndex}`] !== undefined ||
      itemAny[`supplement_stock_trade_name_${productIndex}`] !== undefined ||
      itemAny[`supplement_stock_balance_${productIndex}`] !== undefined
    ) {
      transformed[`Classificação do produto no depósito ${productIndex}`] = itemAny[`supplement_stock_classification_${productIndex}`] || '';
      transformed[`Nome comercial produto depósito ${productIndex}`] = itemAny[`supplement_stock_trade_name_${productIndex}`] || '';
      transformed[`Quantidade de suplemento no depósito ${productIndex}`] = itemAny[`supplement_stock_balance_${productIndex}`] || 0;
      productIndex++;
    }

    return transformed;
  });
};

export const exportDailySnapshotToExcel = (data: IDailySnapshot[], filename?: string) => {
  if (!data || data.length === 0) {
    alert("Nenhum dado disponível para exportação");
    return;
  }

  const transformedData = transformDataForExport(data);

  // Ordenar por Data (decrescente), Fazenda, Retiro e Pasto
  transformedData.sort((a, b) => {
    const dateA = parseDateString(a.Data);
    const dateB = parseDateString(b.Data);
    if (dateB !== dateA) return dateB > dateA ? -1 : 1;

    const fazendaA = a.Fazenda || '';
    const fazendaB = b.Fazenda || '';
    if (fazendaA !== fazendaB) return fazendaA.localeCompare(fazendaB);

    const retiroA = a.Retiro || '';
    const retiroB = b.Retiro || '';
    if (retiroA !== retiroB) return retiroA.localeCompare(retiroB);

    const pastoA = a.Pasto || '';
    const pastoB = b.Pasto || '';
    return pastoA.localeCompare(pastoB);
  });

  const worksheet = XLSX.utils.json_to_sheet(transformedData);

  // gerar larguras dinamicamente com base no header e no conteúdo da primeira linha
  worksheet['!cols'] = computeColumnWidthsFromFirstRow(transformedData, { padding: 2, min: 8, max: 60 });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Snapshot');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `daily-snapshot-${new Date().toISOString().split('T')[0]}.xlsx`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const transformDataForExportByBatch = (data: IDailySnapshot[]): ITransformedDailySnapshot[] => {
  return data.map((item) => {
    const date = item.snapshot_date.split(' ')[0];
    const month = date.split('-')[1];
    const year = date.split('-')[0];

    const initialsMicroArea = item.micro_area_initials ? `${item.farm_initials}-${item.area_initials}-${item.micro_area_initials}` : "";
    const initialsBatch = item.batch_initials ? `${item.farm_initials}-${item.area_initials}-${item.batch_initials}` : "";

    const transformed: ITransformedDailySnapshot = {
      'Ano': year,
      'Mês': month,
      'Data': formatDateSnapshot(item.snapshot_date),
      'Fazenda': item.farm_name,
      'Retiro': item.area_name,
      'Pasto': initialsMicroArea,
      'Lote': initialsBatch,
      'Grupo de manejo': item.micro_area_management_group_initials,
      'Área do pasto (ha)': item.micro_area_acreage,
      '0a8 F': item.qty_0_8_female,
      '9a12 F': item.qty_9_12_female,
      '13a24 F': item.qty_13_24_female,
      '25a36 F': item.qty_25_36_female,
      '>=37 F': item.qty_37_female,
      '0a8 M': item.qty_0_8_male,
      '9a12 M': item.qty_9_12_male,
      '13a24 M': item.qty_13_24_male,
      '25a36 M': item.qty_25_36_male,
      '>=37 M': item.qty_37_male,
      'Touros': item.qty_adult_male || 0,
      'Quantidade de animais no lote': item.total_from_category,
      'Peso médio': item.average_weight,
      'Peso Total': item.total_weight,
      'UA': item.ua,
      'Tx lotação instantânea': item.instant_capacity_rate,
      'Ganho médio (kg PV/ cab)': item.productivity_average,
      'Ganho total (kg PV)': item.cumulative_productivity,
      'Produtividade (kg PV/ha)': item.productivity,
      'Suplementação no cocho (kg)': item.supplement_trough_quantity_total,
      'Classificação do produto no cocho': item.supplement_trough_classification,
      'Produto no cocho': item.supplement_trough_trade_name
    };

    return transformed;
  });
};

export const exportDailySnapshotByBatchToExcel = (data: IDailySnapshot[], filename?: string) => {
  if (!data || data.length === 0) {
    alert("Nenhum dado disponível para exportação");
    return;
  }

  const transformedData = transformDataForExportByBatch(data);

  // Ordenar por Data (decrescente), Fazenda, Retiro e Lote
  transformedData.sort((a, b) => {
    const dateA = parseDateString(a.Data);
    const dateB = parseDateString(b.Data);
    if (dateB !== dateA) return dateB > dateA ? -1 : 1;

    const fazendaA = a.Fazenda || '';
    const fazendaB = b.Fazenda || '';
    if (fazendaA !== fazendaB) return fazendaA.localeCompare(fazendaB);

    const retiroA = a.Retiro || '';
    const retiroB = b.Retiro || '';
    if (retiroA !== retiroB) return retiroA.localeCompare(retiroB);

    const loteA = a.Lote || '';
    const loteB = b.Lote || '';
    return loteA.localeCompare(loteB);
  });

  const worksheet = XLSX.utils.json_to_sheet(transformedData);

  worksheet['!cols'] = computeColumnWidthsFromFirstRow(transformedData, { padding: 2, min: 8, max: 60 });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Snapshot By Batch');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `daily-snapshot-by-batch-${new Date().toISOString().split('T')[0]}.xlsx`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const transformDataForExportCarbon = (data: IDailySnapshot[]): ITransformedDailySnapshot[] => {
  return data.map((item) => {
    const initialsMicroArea = item.micro_area_initials ? `${item.farm_initials}-${item.area_initials}-${item.micro_area_initials}` : "";
    const initialsBatch = item.batch_initials ? `${item.farm_initials}-${item.area_initials}-${item.batch_initials}` : "";

    const transformed: ITransformedDailySnapshot = {
      'Data': formatDateSnapshot(item.snapshot_date),
      'Fazenda': item.farm_name,
      'Retiro': item.area_name,
      'Pasto': initialsMicroArea,
      'Lote': initialsBatch,
      'Grupo de manejo': item.micro_area_management_group_initials,
      'Area (ha)': item.micro_area_acreage,
      'Número de animais': item.total_from_category,
      'Peso Total (kg)': item.total_weight,
      'Peso Médio (kg/cab)': item.average_weight,
      'UA': item.ua,
      'Ganho total': item.cumulative_productivity,
      'Ganho médio diário (kg/dia)': item.productivity_average,
      'Consumo MS (kg/dia.cab)': item.ms_intake_kg_head_day,
      'Consumo (%PV)': item.intake_percent_body_weight,
      'Consumo MS total (kg)': item.total_ms_intake_kg,
      'Consumo area (kg MS/ha)': item.area_ms_intake_kg_ha,
      'Suplemento ofertado (kg)': item.supplement_offered_accumulated_kg,
      'Consumo médio de suplemento (kg/dia)': item.avg_supplement_intake_kg_day,
      'Consumo médio (kg/cab.dia)': item.avg_intake_kg_head_day,
      'Suplemento na CMS(%)': item.supplement_in_cms_percent,
      'Digestibilidade (%)': item.digestibility_percent,
      'Emissão (g CH4/cab.dia)': item.methane_emission_g_head_day,
      'Emissão total diária (kg CH4/dia)': item.total_methane_emission_kg_day,
      'kg CO2 equivalente': item.co2_equivalent_kg
    };

    return transformed;
  });
};

export const exportDailySnapshotCarbonToExcel = (data: IDailySnapshot[], filename?: string) => {
  if (!data || data.length === 0) {
    alert("Nenhum dado disponível para exportação");
    return;
  }

  const transformedData = transformDataForExportCarbon(data);

  // Ordenar por Data (decrescente), Fazenda, Retiro e Lote
  transformedData.sort((a, b) => {
    const dateA = parseDateString(a.Data);
    const dateB = parseDateString(b.Data);
    if (dateB !== dateA) return dateB > dateA ? -1 : 1;

    const loteA = a.Lote || '';
    const loteB = b.Lote || '';
    if (loteA !== loteB) return loteA.localeCompare(loteB);

    const fazendaA = a.FAZENDA || '';
    const fazendaB = b.FAZENDA || '';
    if (fazendaA !== fazendaB) return fazendaA.localeCompare(fazendaB);

    const retiroA = a.Retiro || '';
    const retiroB = b.Retiro || '';
    if (retiroA !== retiroB) return retiroA.localeCompare(retiroB);

    const pastoA = a.Pasto || '';
    const pastoB = b.Pasto || '';
    return pastoA.localeCompare(pastoB);
  });

  const worksheet = XLSX.utils.json_to_sheet(transformedData);

  worksheet['!cols'] = computeColumnWidthsFromFirstRow(transformedData, { padding: 2, min: 8, max: 60 });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Snapshot By Batch');

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `daily-snapshot-by-batch-${new Date().toISOString().split('T')[0]}.xlsx`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
